/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core'

import { SearchUrlService } from '../../../research/services/search-url.service'

@Component({
  standalone: false,
  selector: 'mitre-card',
  templateUrl: './mitre-card-component.html',
  styleUrls: ['./mitre-card-component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush, // Optimize change detection
})
export class MitreCardComponent implements OnInit, OnChanges {
  private searchUrlService = inject(SearchUrlService)

  @Input() public tactic: any
  @Input() public child: any
  @Input() public cyborgMitreFilter: any
  @Input() public companyDeployedMitreFilter: any
  @Input() public companyStagedMitreFilter: any

  public cyborgTacticTechniqueCoverage = false
  public companyTacticTechniqueCoverage = false
  public toolTipString = ''
  public count = 0
  public sub_techniques_array: Array<{ key: string; doc_count: number }> = []
  public menuVisible = false
  public subMenuVisible = false

  public ngOnInit(): void {
    this.buildToolTip()
    this.getCyborgMITRECoverage()
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.companyTacticTechniqueCoverage = false
    if (changes.companyStagedMitreFilter?.currentValue) {
      this.getCompanyStagedMITRECoverage()
    }
    if (changes.companyDeployedMitreFilter?.currentValue) {
      this.getCompanyDeployedMITRECoverage()
    }
  }

  public trackByFn(
    index: number,
    item: { key: string; doc_count: number },
  ): string {
    return item.key // Use the 'key' property as the unique identifier
  }

  private getCyborgMITRECoverage(): void {
    const tacticAgg = this.cyborgMitreFilter.find(
      (tacticAgg) =>
        tacticAgg.key.toLowerCase() ===
        this.tactic.x_mitre_shortname.replace(/-/gi, ' '),
    )

    if (tacticAgg) {
      const techniqueFound = tacticAgg.techniques.buckets.find((item) => {
        if (item.key === this.child.name) {
          this.count = item.doc_count
          this.sub_techniques_array = this.getSubTechniques(
            tacticAgg.techniques.buckets,
          )
          return true
        }
        return this.child.sub_techniques.some(
          (sub) => sub.name === this.child.name,
        )
      })

      this.cyborgTacticTechniqueCoverage = Boolean(techniqueFound)
    }
  }

  private getCompanyStagedMITRECoverage(): void {
    if (this.cyborgTacticTechniqueCoverage) {
      const tacticMatch = this.companyStagedMitreFilter.stagedTactics.includes(
        this.tactic.x_mitre_shortname.replace(/-/gi, ' '),
      )
      const techniqueMatch = this.isTechniqueCovered(
        this.companyStagedMitreFilter.stagedTechniques,
      )

      this.companyTacticTechniqueCoverage = tacticMatch && techniqueMatch
    }
  }

  private getCompanyDeployedMITRECoverage(): void {
    if (this.cyborgTacticTechniqueCoverage) {
      const tacticMatch =
        this.companyDeployedMitreFilter.deployedTactics.includes(
          this.tactic.x_mitre_shortname.replace(/-/gi, ' '),
        )
      const techniqueMatch = this.isTechniqueCovered(
        this.companyDeployedMitreFilter.deployedTechniques,
      )

      this.companyTacticTechniqueCoverage = tacticMatch && techniqueMatch
    }
  }

  private isTechniqueCovered(techniques: string[]): boolean {
    return (
      techniques.includes(this.child.name) ||
      this.child.sub_techniques.some((sub) => techniques.includes(sub.name))
    )
  }

  private getSubTechniques(buckets: any[]): any[] {
    return this.child.sub_techniques
      .map((subtech) =>
        buckets.filter((subitem) => subtech.name === subitem.key),
      )
      .flat()
  }

  private buildToolTip(): void {
    const childTechId = this.child.external_references.find(
      (item) => item.source_name === 'mitre-attack',
    )

    const subString = this.child.sub_techniques
      .map((item) => {
        const subTechId = item.external_references.find(
          (subItem) => subItem.source_name === 'mitre-attack',
        )
        return `${item.name} - ${subTechId.external_id}`
      })
      .join('\n')

    this.toolTipString = `MITRE Technique ID: ${childTechId.external_id}\nSub Techniques:\n${subString}`
  }

  public onFilter(technique: string): void {
    this.searchUrlService.gotoUrl({
      filters: {
        mitre_technique_names: [technique],
      },
    })
  }
}
